/**
 * Auto-Refresh Hook with Session Persistence
 * 
 * Provides auto-refresh functionality for log pages with state persisted
 * to sessionStorage so settings survive page navigation within the session.
 */

import { useState, useEffect, useCallback } from 'react';

interface AutoRefreshConfig {
  /** Unique key for sessionStorage (e.g., 'logs_autorefresh') */
  key: string;
  /** Default enabled state if not in sessionStorage */
  defaultEnabled?: boolean;
  /** Default interval in ms if not in sessionStorage */
  defaultInterval?: number;
}

interface AutoRefreshState {
  /** Whether auto-refresh is enabled */
  enabled: boolean;
  /** Set enabled state */
  setEnabled: (enabled: boolean) => void;
  /** Refresh interval in milliseconds */
  interval: number;
  /** Set interval in milliseconds */
  setInterval: (interval: number) => void;
  /** Toggle enabled state */
  toggle: () => void;
}

/**
 * Hook for managing auto-refresh state with session persistence
 * 
 * @example
 * ```tsx
 * const autoRefresh = useAutoRefresh({ 
 *   key: 'logs_autorefresh',
 *   defaultEnabled: false,
 *   defaultInterval: 5000 
 * });
 * 
 * // Use in useQuery
 * useQuery({
 *   queryKey: ['logs'],
 *   queryFn: fetchLogs,
 *   refetchInterval: autoRefresh.enabled ? autoRefresh.interval : false,
 * });
 * 
 * // Toggle button
 * <button onClick={autoRefresh.toggle}>
 *   Auto-refresh {autoRefresh.enabled ? 'ON' : 'OFF'}
 * </button>
 * ```
 */
export function useAutoRefresh({ 
  key, 
  defaultEnabled = false, 
  defaultInterval = 5000 
}: AutoRefreshConfig): AutoRefreshState {
  // Load initial state from sessionStorage
  const [enabled, setEnabledState] = useState(() => {
    if (typeof window === 'undefined') return defaultEnabled;
    const stored = sessionStorage.getItem(`${key}_enabled`);
    return stored !== null ? stored === 'true' : defaultEnabled;
  });
  
  const [interval, setIntervalState] = useState(() => {
    if (typeof window === 'undefined') return defaultInterval;
    const stored = sessionStorage.getItem(`${key}_interval`);
    return stored !== null ? parseInt(stored, 10) : defaultInterval;
  });

  // Persist enabled state to sessionStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(`${key}_enabled`, String(enabled));
  }, [key, enabled]);

  // Persist interval to sessionStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(`${key}_interval`, String(interval));
  }, [key, interval]);

  // Wrapped setters that update both state and storage
  const setEnabled = useCallback((value: boolean) => {
    setEnabledState(value);
  }, []);

  const setInterval = useCallback((value: number) => {
    setIntervalState(value);
  }, []);

  const toggle = useCallback(() => {
    setEnabledState(prev => !prev);
  }, []);

  return {
    enabled,
    setEnabled,
    interval,
    setInterval,
    toggle,
  };
}

/** Available refresh interval options */
export const REFRESH_INTERVALS = [
  { value: 1000, label: '1 second' },
  { value: 2000, label: '2 seconds' },
  { value: 5000, label: '5 seconds' },
  { value: 10000, label: '10 seconds' },
  { value: 30000, label: '30 seconds' },
  { value: 60000, label: '1 minute' },
] as const;

/** Format interval for display */
export function formatInterval(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${ms / 1000}s`;
  return `${ms / 60000}min`;
}
